import Decimal from 'decimal.js'
import {
    LoyaltyRedeemPointRuleSettingContract,
    OrderLineLookupContract,
    OrderLookupContract,
} from 'models'
import { useMemo, useCallback, useState } from 'react'
import { sumDecimal } from 'utilities/decimalsManipulate'
import {
    adjustSplitedValue,
    ceil,
    round,
} from 'views/Sale/utils/orderCalculation/common'

export const useRedeemPointCalculation = ({
    pointRedemptionSetting,
    orderDetail,
}: {
    pointRedemptionSetting: LoyaltyRedeemPointRuleSettingContract | undefined
    orderDetail?: OrderLookupContract
}) => {
    const requiredPointToRedeem = useMemo<number>(
        () => pointRedemptionSetting?.requiredPoint ?? 0,
        [pointRedemptionSetting],
    )

    const maxDiscount = useMemo(
        () =>
            round(
                new Decimal(
                    (orderDetail?.totalDueAmount ?? 0) -
                        //exclude tax exclusive and service charge
                        (orderDetail?.serviceChargeAmount ?? 0) -
                        (orderDetail?.taxExclusiveAmount ?? 0),
                ),
            ),
        [
            orderDetail?.serviceChargeAmount,
            orderDetail?.taxExclusiveAmount,
            orderDetail?.totalDueAmount,
        ],
    )
    const monetaryValue = useMemo(
        () => pointRedemptionSetting?.monetaryValue ?? 0,
        [pointRedemptionSetting?.monetaryValue],
    )
    const requiredPoint = useMemo(
        () => pointRedemptionSetting?.requiredPoint ?? 0,
        [pointRedemptionSetting?.requiredPoint],
    )

    const calculateRedeemedPointToDiscount = useCallback(
        (pointToRedeem: Decimal) => {
            const result = pointToRedeem
                .mul(monetaryValue)
                .div(requiredPoint)
                .toNearest(0.01, 2)
                .toNumber()
            return result
        },
        [requiredPoint, monetaryValue],
    )

    const calculateDiscountToPoint = useCallback(
        (discount: Decimal) => {
            return discount.mul(requiredPoint).div(monetaryValue).toNumber()
        },
        [requiredPoint, monetaryValue],
    )
    const [newTotal, setNewTotal] = useState(orderDetail?.totalDueAmount ?? 0)

    const calculateNewTotalDueAfterDisocunt = useCallback(
        (discount: Decimal) => {
            if ((orderDetail?.totalDueAmount ?? 0) === 0) return 0
            //calculation discount for each orderline
            //TODO: update to support combo and modifier tax
            var lineDiscounts = (orderDetail?.orderLines ?? []).map(
                (o: OrderLineLookupContract) => {
                    let orderlineTotalDue = new Decimal(o.itemNetSubtotal ?? 0)

                    if (o.isTaxInclusive) {
                        orderlineTotalDue = orderlineTotalDue.plus(
                            o?.totalTaxAmount ?? 0,
                        )
                    }
                    let lineRatio = ceil(
                        orderlineTotalDue.div(orderDetail?.subtotal!),
                    )
                    let lineDiscount = discount.mul(lineRatio)
                    return ceil(lineDiscount)
                },
            )
            adjustSplitedValue(lineDiscounts, discount)
            var newLineTotals = lineDiscounts.map((lineDiscount, i) => {
                let o = orderDetail!.orderLines![i]!
                //use netSubtotal for get final total (after discount)
                let orderlineTotalDue = new Decimal(o.itemNetSubtotal ?? 0)

                //re-add tax
                if (o.isTaxInclusive) {
                    orderlineTotalDue = orderlineTotalDue.plus(
                        o?.totalTaxAmount ?? 0,
                    )
                }

                let totalDueAfterRedeemDiscount = orderlineTotalDue.minus(
                    lineDiscount,
                )

                //calculation new tax exclusive amount
                if (!o.isTaxInclusive) {
                    var newTax = totalDueAfterRedeemDiscount
                        .mul(o.taxRate ?? 0)
                        .div(100)
                    totalDueAfterRedeemDiscount = totalDueAfterRedeemDiscount.plus(
                        newTax,
                    )
                }

                //add service charge to line ( service charge always not discount from redeem point)
                totalDueAfterRedeemDiscount = totalDueAfterRedeemDiscount.plus(
                    o?.serviceChargeAmount ?? 0,
                )
                if (!orderDetail?.isServiceChargeTaxInclusive)
                    totalDueAfterRedeemDiscount = totalDueAfterRedeemDiscount.plus(
                        o?.serviceChargeTaxAmount ?? 0,
                    )

                return round(totalDueAfterRedeemDiscount)
            })

            var newOrderTotalDue = sumDecimal(newLineTotals)
            setNewTotal(newOrderTotalDue.toNumber())
            return newOrderTotalDue
        },
        [orderDetail],
    )
    const floorPoint = useCallback(
        (point: number) => {
            return point - (point % requiredPointToRedeem)
        },
        [requiredPointToRedeem],
    )
    return {
        calculateNewTotalDueAfterDisocunt,
        calculateDiscountToPoint,
        calculateRedeemedPointToDiscount,
        newTotal,
        maxDiscount,
        floorPoint,
        requiredPointToRedeem,
    }
}
